/*
 * TenantSitePage — public branded site for a MyLoanOfficer.ai customer.
 *
 * Route: /site/:slug
 *
 * Fetches /api/tenants/public/:slug and renders one of 3 templates:
 *   modern  — the default TCL-style layout (light, spacious, serif headings)
 *   classic — centered hero, warmer tones, more traditional
 *   bold    — dark hero with vibrant accents
 *
 * Colors + copy come from the tenant's brand data. The templates share
 * the same content sections (hero, features, testimonials, contact),
 * but with distinct visual identities.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { applyBranding, brandAssetUrl } from './branding';
import { getToken } from './portal/api';
import { PAGE_PAUSED_COPY, isLinkPaused } from './portal/errors';

import { formatPhone, telHref } from './portal/shared/phone';
import {
  ArrowRight, Star, Phone, Mail, MapPin, Sparkles, Check,
  Calculator, FileCheck, Users, Bot, Briefcase, FileText, Home,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// The most feature cards any template shows; the Brand page's repeater stops here too.
export const MAX_FEATURES = 4;

const iconMap = {
  calculator: Calculator, 'file-check': FileCheck, users: Users,
  sparkles: Sparkles, 'link-2': ArrowRight, briefcase: Briefcase,
  file: FileText, home: Home, bot: Bot,
};

// The icons a feature card can use, in the order the Brand page's picker lists
// them. An unknown or empty key falls back to Sparkles.
export const ICON_OPTIONS = [
  ['sparkles', 'Sparkles'], ['calculator', 'Calculator'], ['file-check', 'Checklist'],
  ['users', 'People'], ['briefcase', 'Briefcase'], ['file', 'Document'],
  ['home', 'Home'], ['bot', 'Assistant'], ['link-2', 'Arrow'],
];

const initialsOf = (t) => (t.initials || (t.name || '').split(/\s+/).filter(Boolean).slice(0, 2)
  .map((w) => w[0].toUpperCase()).join(''));

// Reviews section shared by all three templates. Modern passes its own heading;
// the others use the neutral one.
function Testimonials({ tenant, heading = 'What clients say', fontFamily, className = '' }) {
  const c = tenant.colors || {};
  const items = tenant.content?.testimonials || [];
  if (items.length === 0) return null;
  return (
    <section id="testimonials" className={`py-20 px-6 ${className}`}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: c.accent }}>What clients say</div>
          <h2 className="font-bold" style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', color: c.primary, fontFamily }}>
            {heading}
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {items.map((t, i) => (
            <div key={i} className="p-6 rounded-2xl bg-white border border-slate-100" data-testid="site-testimonial">
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, x) => <Star key={x} size={14} style={{ color: c.accent, fill: c.accent }} />)}
              </div>
              <p className="text-sm leading-relaxed mb-4" style={{ color: c.primary }}>"{t.quote}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm text-white"
                     style={{ background: c.primary }}>{initialsOf(t)}</div>
                <div>
                  <div className="font-semibold text-sm" style={{ color: c.primary }}>{t.name}</div>
                  <div className="text-xs text-slate-500">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


// Placeholder wording until counsel approves the real text (Ken list item 33). The
// form itself stays off until TENANT_SITE_LEAD_FORM_ENABLED is set on the backend.
const siteConsentText = (name) => `I agree that ${name} may contact me by phone, text or email about my inquiry.`;
// Same limits the server enforces on a public submit (ticket 303), so a person cannot type past them.
const FIELD_MAX = { first_name: 120, last_name: 120, email: 254, phone: 32 };
export const maxLengthFor = (key) => (Object.hasOwn(FIELD_MAX, key) ? FIELD_MAX[key] : 2000);
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'];

// Contact form shared by all three templates. Renders only when the backend sent a
// `lead_form` (switch on, tenant picked one of its own live forms). Posts to the
// lead-gen submit endpoint; the consent box starts unticked and gates the button.
function SiteLeadForm({ tenant }) {
  const form = tenant.lead_form;
  const c = tenant.colors || {};
  const [values, setValues] = useState({});
  const [consent, setConsent] = useState(false);
  const [hp, setHp] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | done | error | paused
  const consentText = useMemo(() => siteConsentText(tenant.name), [tenant.name]);
  if (!form || !(form.fields || []).length) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!consent || status === 'sending') return;
    setStatus('sending');
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    UTM_KEYS.forEach((k) => { if (params.get(k)) utm[k] = params.get(k); });
    try {
      await axios.post(`${API}/leadgen/submit/${form.slug}`, {
        source: 'tenant_site', ...utm, ...values, consent: true, consent_text: consentText, _hp: hp,
      });
      setStatus('done');
    } catch (e) {
      setStatus(isLinkPaused(e) ? 'paused' : 'error');
    }
  };

  if (status === 'done') {
    return (
      <div className="mt-10 max-w-xl mx-auto p-6 rounded-2xl bg-white text-center" data-testid="site-lead-form-done">
        <Check size={22} className="mx-auto mb-2" style={{ color: c.accent }} />
        <div className="font-semibold" style={{ color: c.primary }}>Thanks. We got your message.</div>
      </div>
    );
  }
  const input = 'w-full px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-900 focus:outline-none';
  return (
    <form onSubmit={submit} className="mt-10 max-w-xl mx-auto p-6 rounded-2xl bg-white text-left text-slate-800 space-y-3"
          data-testid="site-lead-form">
      {form.fields.map((f) => (
        <div key={f.key}>
          <label htmlFor={`site-lead-${f.key}`} className="text-xs font-semibold text-slate-600 block mb-1">
            {f.label}{f.required ? ' *' : ''}
          </label>
          {f.type === 'textarea'
            ? <textarea id={`site-lead-${f.key}`} rows={3} maxLength={maxLengthFor(f.key)} required={!!f.required} className={input}
                        value={values[f.key] || ''} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} />
            : <input id={`site-lead-${f.key}`} type={f.type || 'text'} maxLength={maxLengthFor(f.key)} required={!!f.required} className={input}
                     value={values[f.key] || ''} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} />}
        </div>
      ))}
      {/* honeypot: hidden from people, bots fill it and the server drops the submit */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px' }}>
        <input type="text" name="_hp" tabIndex={-1} autoComplete="off" value={hp}
               onChange={(e) => setHp(e.target.value)} data-testid="site-lead-hp" />
      </div>
      <label className="flex items-start gap-2 text-xs text-slate-600">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
               className="mt-0.5" data-testid="site-lead-consent" />
        <span>{consentText}</span>
      </label>
      {(status === 'error' || status === 'paused') && (
        <div className="text-sm text-red-600" role="alert" data-testid="site-lead-error">
          {status === 'paused' ? PAGE_PAUSED_COPY : 'Could not send that. Try again in a moment.'}
        </div>
      )}
      <button type="submit" disabled={!consent || status === 'sending'} data-testid="site-lead-submit"
              className="w-full px-4 py-2.5 rounded-lg font-semibold text-sm text-white disabled:opacity-50"
              style={{ background: c.primary }}>
        {status === 'sending' ? 'Sending…' : (tenant.content?.hero_cta_primary || 'Send')}
      </button>
    </form>
  );
}


// ============================================================================
// MODERN template — light, serif, spacious (default TCL style)
// ============================================================================
function ModernTemplate({ tenant }) {
  const c = tenant.colors || {};
  return (
    <div className="min-h-screen bg-white" style={{ '--primary': c.primary, '--accent': c.accent, '--highlight': c.highlight }}>
      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {tenant.logo_url && <img src={brandAssetUrl(tenant.logo_url)} alt={tenant.name} className="h-9 max-w-[140px] object-contain" />}
            {!tenant.logo_url && <div className="font-bold text-lg" style={{ color: c.primary }}>{tenant.name}</div>}
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-sm font-semibold" style={{ color: c.primary }}>Services</a>
            <a href="#testimonials" className="text-sm font-semibold" style={{ color: c.primary }}>Reviews</a>
            <a href="#contact" className="text-sm font-semibold" style={{ color: c.primary }}>Contact</a>
          </div>
          <a href="#contact" className="px-4 py-2 rounded-lg font-semibold text-sm text-white hover:opacity-90 transition"
             style={{ background: c.primary }}>{tenant.content?.hero_cta_primary || 'Get started'}</a>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-20 pb-24 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-6"
               style={{ background: `${c.accent}22`, color: c.primary }}>
            <Sparkles size={12} style={{ color: c.accent }} />
            {tenant.product_type === 'real_estate' ? 'Real Estate' : 'Mortgage Lending'}
          </div>
          <h1 className="font-bold tracking-tight leading-[1.05]" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)', color: c.primary, fontFamily: '"Playfair Display", serif' }}>
            {tenant.content?.hero_headline}
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-lg text-slate-600 leading-relaxed">
            {tenant.content?.hero_subhead}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <a href="#contact" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg font-semibold text-white hover:shadow-xl transition"
               style={{ background: c.primary }}>
              {tenant.content?.hero_cta_primary} <ArrowRight size={16} />
            </a>
            {tenant.content?.contact_phone && tenant.content?.hero_cta_secondary && (
              <a href={telHref(tenant.content.contact_phone)} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg font-semibold border border-slate-200 hover:bg-slate-50 transition"
                 style={{ color: c.primary }} data-testid="site-secondary-cta">
                <Phone size={16} /> {tenant.content.hero_cta_secondary}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="services" className="py-20 px-6" style={{ background: '#F8FAFC' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: c.accent }}>What we do</div>
            <h2 className="font-bold" style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)', color: c.primary, fontFamily: '"Playfair Display", serif' }}>
              {tenant.product_type === 'real_estate' ? 'Your real estate partner.' : 'Loans, done right.'}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(tenant.content?.features || []).slice(0, MAX_FEATURES).map((f, i) => {
              const Ic = iconMap[f.icon] || Sparkles;
              return (
                <div key={i} className="p-6 rounded-2xl bg-white border border-slate-100 hover:shadow-md transition">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                       style={{ background: `${c.accent}22`, color: c.accent }}>
                    <Ic size={18} />
                  </div>
                  <div className="font-bold mb-2" style={{ color: c.primary }}>{f.title}</div>
                  <div className="text-sm text-slate-600">{f.body}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Testimonials tenant={tenant} heading="Trusted by hundreds." fontFamily={'"Playfair Display", serif'} />

      {/* Contact */}
      <section id="contact" className="py-20 px-6" style={{ background: c.primary }}>
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="font-bold" style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)', fontFamily: '"Playfair Display", serif' }}>
            Let's talk.
          </h2>
          <p className="mt-3 opacity-80">Reach out. We reply within one business day.</p>
          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            {tenant.content?.contact_phone && (
              <a href={telHref(tenant.content.contact_phone)} className="p-4 rounded-xl transition hover:bg-white/10"
                 style={{ background: 'rgba(255,255,255,0.05)' }}>
                <Phone size={20} className="mx-auto mb-2" style={{ color: c.accent }} />
                <div className="font-semibold">{formatPhone(tenant.content.contact_phone)}</div>
              </a>
            )}
            {tenant.content?.contact_email && (
              <a href={`mailto:${tenant.content.contact_email}`} className="p-4 rounded-xl transition hover:bg-white/10"
                 style={{ background: 'rgba(255,255,255,0.05)' }}>
                <Mail size={20} className="mx-auto mb-2" style={{ color: c.accent }} />
                <div className="font-semibold">{tenant.content.contact_email}</div>
              </a>
            )}
            {tenant.content?.contact_address && (
              <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <MapPin size={20} className="mx-auto mb-2" style={{ color: c.accent }} />
                <div className="font-semibold text-sm">{tenant.content.contact_address}</div>
              </div>
            )}
          </div>
          <SiteLeadForm tenant={tenant} />
        </div>
      </section>

      <Footer tenant={tenant} />
    </div>
  );
}


// ============================================================================
// CLASSIC template — centered, warm, traditional
// ============================================================================
function ClassicTemplate({ tenant }) {
  const c = tenant.colors || {};
  return (
    <div className="min-h-screen" style={{ background: '#FDFBF6' }}>
      <nav className="border-b" style={{ borderColor: `${c.primary}22` }}>
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          {tenant.logo_url ? <img src={brandAssetUrl(tenant.logo_url)} alt={tenant.name} className="h-11 object-contain" /> : <div className="font-bold text-xl" style={{ color: c.primary, fontFamily: 'Georgia, serif' }}>{tenant.name}</div>}
          <div className="text-sm" style={{ color: c.primary }}>
            {tenant.content?.contact_phone && <a href={telHref(tenant.content.contact_phone)} className="mr-4">{formatPhone(tenant.content.contact_phone)}</a>}
            {tenant.content?.contact_email && <a href={`mailto:${tenant.content.contact_email}`}>{tenant.content.contact_email}</a>}
          </div>
        </div>
      </nav>
      <section className="py-24 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="text-xs uppercase tracking-[0.3em] mb-6 font-bold" style={{ color: c.accent }}>
            Est. {new Date().getFullYear() - 5}
          </div>
          <h1 className="font-bold leading-tight" style={{ fontSize: 'clamp(2.5rem, 5.5vw, 4.5rem)', color: c.primary, fontFamily: 'Georgia, serif' }}>
            {tenant.content?.hero_headline}
          </h1>
          <div className="w-16 h-0.5 mx-auto my-8" style={{ background: c.accent }} />
          <p className="text-lg text-slate-600 leading-relaxed">{tenant.content?.hero_subhead}</p>
          <a href="#contact" className="mt-10 inline-block px-8 py-4 rounded-none font-semibold text-white transition hover:opacity-90"
             style={{ background: c.primary, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: 13 }}>
            {tenant.content?.hero_cta_primary}
          </a>
        </div>
      </section>
      <section className="py-16 px-6 border-t" style={{ borderColor: `${c.primary}22` }}>
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
          {(tenant.content?.features || []).slice(0, MAX_FEATURES).map((f, i) => {
            const Ic = iconMap[f.icon] || Sparkles;
            return (
              <div key={i} className="flex gap-4">
                <div className="flex-shrink-0"><Ic size={22} style={{ color: c.accent }} /></div>
                <div>
                  <div className="font-bold mb-1" style={{ color: c.primary, fontFamily: 'Georgia, serif' }}>{f.title}</div>
                  <div className="text-sm text-slate-600 leading-relaxed">{f.body}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <Testimonials tenant={tenant} fontFamily="Georgia, serif" className="border-t" />
      <section id="contact" className="py-20 px-6 border-t text-center" style={{ borderColor: `${c.primary}22` }}>
        <div className="max-w-2xl mx-auto">
          <h2 className="font-bold" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', color: c.primary, fontFamily: 'Georgia, serif' }}>Reach out today.</h2>
          <div className="mt-8 text-slate-600">
            {tenant.content?.contact_phone && <div className="mb-1">{formatPhone(tenant.content.contact_phone)}</div>}
            {tenant.content?.contact_email && <div>{tenant.content.contact_email}</div>}
          </div>
          <SiteLeadForm tenant={tenant} />
        </div>
      </section>
      <Footer tenant={tenant} />
    </div>
  );
}


// ============================================================================
// BOLD template — dark hero, vibrant accents, modern SaaS feel
// ============================================================================
function BoldTemplate({ tenant }) {
  const c = tenant.colors || {};
  return (
    <div className="min-h-screen bg-white">
      {/* Dark hero */}
      <section className="relative overflow-hidden text-white" style={{ background: c.primary }}>
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-20 blur-3xl"
             style={{ background: c.accent, transform: 'translate(30%, -30%)' }} />
        <nav className="relative px-6 py-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            {tenant.logo_url ? <img src={brandAssetUrl(tenant.logo_url)} alt={tenant.name} className="h-10 object-contain bg-white/10 rounded px-2 py-1" /> : <div className="font-bold text-xl">{tenant.name}</div>}
            <a href="#contact" className="px-4 py-2 rounded-lg font-semibold text-sm hover:opacity-90 transition"
               style={{ background: c.accent, color: c.primary }}>{tenant.content?.hero_cta_primary}</a>
          </div>
        </nav>
        <div className="relative px-6 py-24">
          <div className="max-w-5xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold mb-6"
                 style={{ background: `${c.accent}33`, color: c.accent, border: `1px solid ${c.accent}55` }}>
              <Sparkles size={12} />
              {tenant.product_type === 'real_estate' ? 'Real Estate — Reimagined' : 'Mortgage — Reimagined'}
            </div>
            <h1 className="font-black leading-[0.95] tracking-tight" style={{ fontSize: 'clamp(3rem, 7vw, 6rem)' }}>
              {tenant.content?.hero_headline}
            </h1>
            <p className="mt-6 max-w-2xl text-lg opacity-80">{tenant.content?.hero_subhead}</p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a href="#contact" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg font-bold hover:shadow-xl transition"
                 style={{ background: c.accent, color: c.primary }}>
                {tenant.content?.hero_cta_primary} <ArrowRight size={16} />
              </a>
              {tenant.content?.contact_phone && tenant.content?.hero_cta_secondary && (
                <a href={telHref(tenant.content.contact_phone)} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg font-semibold border border-white/30 hover:bg-white/10 transition"
                   data-testid="site-secondary-cta">
                  <Phone size={16} /> {tenant.content.hero_cta_secondary}
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section id="services" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <div className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: c.accent }}>Capabilities</div>
            <h2 className="font-bold" style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)', color: c.primary }}>
              Everything you need. Nothing you don't.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {(tenant.content?.features || []).slice(0, MAX_FEATURES).map((f, i) => {
              const Ic = iconMap[f.icon] || Sparkles;
              return (
                <div key={i} className="p-6 rounded-2xl border-2 hover:scale-[1.01] transition"
                     style={{ borderColor: `${c.primary}11`, background: '#fff' }}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                         style={{ background: c.primary, color: c.accent }}>
                      <Ic size={22} />
                    </div>
                    <div>
                      <div className="font-bold text-lg mb-1" style={{ color: c.primary }}>{f.title}</div>
                      <div className="text-sm text-slate-600 leading-relaxed">{f.body}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Testimonials tenant={tenant} className="bg-slate-50" />

      {/* Contact strip */}
      <section id="contact" className="py-16 px-6" style={{ background: c.primary }}>
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="font-bold mb-8" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>Ready to start?</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {tenant.content?.contact_phone && (
              <a href={telHref(tenant.content.contact_phone)} className="px-5 py-3 rounded-lg font-bold hover:opacity-90"
                 style={{ background: c.accent, color: c.primary }}>
                <Phone size={14} className="inline mr-2" />
                {formatPhone(tenant.content.contact_phone)}
              </a>
            )}
            {tenant.content?.contact_email && (
              <a href={`mailto:${tenant.content.contact_email}`} className="px-5 py-3 rounded-lg font-semibold border border-white/30 hover:bg-white/10">
                <Mail size={14} className="inline mr-2" />
                {tenant.content.contact_email}
              </a>
            )}
          </div>
          <SiteLeadForm tenant={tenant} />
        </div>
      </section>
      <Footer tenant={tenant} />
    </div>
  );
}


function Footer({ tenant }) {
  const c = tenant.colors || {};
  return (
    <footer className="py-8 px-6 text-center border-t bg-white" style={{ borderColor: '#e2e8f0' }}>
      <div className="text-xs text-slate-500">
        {tenant.content?.footer_tagline || `© ${new Date().getFullYear()} ${tenant.name}. All rights reserved.`}
        {tenant.content?.nmls && <span className="ml-2">· {tenant.content.nmls}</span>}
      </div>
      <div className="mt-2 text-[10px] text-slate-400">
        Powered by <Link to="/" style={{ color: c.accent }}>BrokerForYou</Link>
      </div>
    </footer>
  );
}


// ============================================================================
// Main
// ============================================================================
export default function TenantSitePage() {
  const { slug } = useParams();
  const wantsPreview = new URLSearchParams(useLocation().search).get('preview') === '1';
  const [tenant, setTenant] = useState(null);
  const [err, setErr] = useState(null);
  const [preview, setPreview] = useState(null); // null | 'member' | 'owner'

  useEffect(() => {
    const loadPublic = () => axios.get(`${API}/tenants/public/${slug}`)
      .then((r) => setTenant(r.data))
      .catch((e) => setErr(e.response?.status === 404 ? 'not_found' : isLinkPaused(e) ? 'paused' : 'error'));
    // "Open full preview" on the Brand page (ticket 203): a signed-in member of
    // this tenant (GET /tenants/mine already shows them this data) sees the site
    // as saved, published or not. Anyone else, or a
    // failed lookup, gets the public site.
    // The platform owner's "View site" link (ticket 205) reaches any tenant's
    // saved site the same way, through the owner-only site-preview route.
    if (wantsPreview && getToken()) {
      const headers = { Authorization: `Bearer ${getToken()}` };
      const loadOwnerPreview = () => axios.get(`${API}/tenants/admin/site-preview/${slug}`, { headers })
        .then((r) => {
          setTenant(r.data);
          setPreview('owner');
        })
        .catch(loadPublic);
      axios.get(`${API}/tenants/mine`, { headers })
        .then((r) => {
          if (r.data?.slug !== slug) return loadOwnerPreview();
          setTenant(r.data);
          setPreview('member');
          return null;
        })
        .catch(loadPublic);
      return;
    }
    loadPublic();
  }, [slug, wantsPreview]);

  useEffect(() => {
    if (tenant) applyBranding({ title: tenant.name, favicon: tenant.favicon_url });
  }, [tenant]);

  if (err === 'not_found') return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="text-6xl mb-4">🚧</div>
        <div className="text-2xl font-bold text-slate-800 mb-2">Site not found</div>
        <div className="text-slate-500">This tenant site doesn't exist or hasn't been published yet.</div>
        <Link to="/" className="mt-6 inline-block px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold">Go to BrokerForYou</Link>
      </div>
    </div>
  );
  if (err === 'paused') return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 text-center" data-testid="site-paused">
      <div className="text-slate-500">{PAGE_PAUSED_COPY}</div>
    </div>
  );
  if (!tenant) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-slate-400">Loading…</div></div>;

  return (
    <>
      {preview && (
        <div className="sticky top-0 z-50 px-4 py-2 text-center text-xs font-semibold bg-amber-100 text-amber-900 border-b border-amber-300"
             data-testid="site-preview-banner">
          {preview === 'owner' ? 'Owner preview of the saved site' : 'Preview of your saved site'}
          {tenant.published ? '' : ' — it is a draft, so visitors cannot see it yet'}.
          {preview === 'owner' ? '' : ' Publish it from Brand & Website.'}
        </div>
      )}
      <TenantSiteRenderer tenant={tenant} />
    </>
  );
}

// ============================================================================
// Shared renderer — used by both the public /site/:slug page and the
// live-preview panel in BrandingSettingsPage.
// ============================================================================
export function TenantSiteRenderer({ tenant }) {
  const T = { modern: ModernTemplate, classic: ClassicTemplate, bold: BoldTemplate }[tenant.template] || ModernTemplate;
  return <T tenant={tenant} />;
}
